import type { WorkspaceMembershipKind } from '@affine/graphql';
/**
 * Shared types for the modular PermissionEditor component.
 * Used across Doc, Folder, and Credential permission UIs.
 */

// =============================================================================
// Permission Entries (what's displayed in the granted list)
// =============================================================================

export interface PermissionUserEntry {
  type: 'user';
  id: string; // role assignment ID (for deletion)
  targetId: string; // userId
  name: string;
  email: string;
  avatarUrl: string | null;
  role: string;
  membershipKind?: WorkspaceMembershipKind | null;
  isAreaBlocked?: boolean;
  areaBoundaryKind?: string | null;
  areaBoundaryRootFolderId?: string | null;
  areaBoundaryRootName?: string | null;
}

export interface PermissionGroupEntry {
  type: 'group';
  id: string; // role assignment ID (for deletion)
  targetId: string; // groupId
  name: string;
  role: string;
  isAreaBlocked?: boolean;
  areaBoundaryKind?: string | null;
  areaBoundaryRootFolderId?: string | null;
  areaBoundaryRootName?: string | null;
}

/** A workspace Mate with a direct role on the object. */
export interface PermissionMateEntry {
  type: 'mate';
  id: string; // mateId (a Mate holds one direct role per object)
  targetId: string; // mateId
  name: string;
  role: string;
  isAreaBlocked?: boolean;
  areaBoundaryKind?: string | null;
  areaBoundaryRootFolderId?: string | null;
  areaBoundaryRootName?: string | null;
}

export type PermissionEntry =
  | PermissionUserEntry
  | PermissionGroupEntry
  | PermissionMateEntry;

export interface PermissionEditorChangeEvent {
  action: 'grant' | 'revoke' | 'update';
  role: string | null;
  targetId: string;
  targetEmail?: string;
  targetName?: string;
  type: PermissionEntry['type'];
}

/**
 * The roles a Mate may hold on a page or folder: a person's roles below
 * Owner (Manager on a page, Admin on a folder at most).
 */
export const MATE_GRANTABLE_ROLES: ReadonlySet<string> = new Set([
  'Admin',
  'Manager',
  'Editor',
  'Commenter',
  'Reader',
]);

// =============================================================================
// Members / Groups (for the invite section)
// =============================================================================

export interface MemberInfo {
  id: string;
  name: string;
  email: string;
  avatarUrl: string | null;
  membershipKind?: WorkspaceMembershipKind | null;
}

export interface GroupInfo {
  id: string;
  name: string;
  description?: string;
}

export interface MateInfo {
  id: string;
  name: string;
}

// =============================================================================
// Permission Editor Configuration
// =============================================================================

export interface PermissionEditorConfig {
  /** All roles (including owner if applicable) — for the role dropdown on existing entries */
  allRoles: readonly string[];

  /** Roles that can be directly granted (usually excludes Owner) */
  grantableRoles: readonly string[];

  /** If present, guest members can only receive these object roles. */
  guestAllowedRoles?: readonly string[];

  /** The owner role string, if ownership transfer is supported. Omit for flat hierarchies. */
  ownerRole?: string;

  /** Role the owner gets demoted to when transferring ownership */
  demoteOnTransferRole?: string;

  /** Default role when granting new access */
  defaultGrantRole: string;

  /** Existing roles rendered as labels rather than editable dropdowns. */
  immutableRoles?: readonly string[];

  /** Optional object-permission label overrides by role value */
  roleLabelKeys?: Readonly<Record<string, string>>;

  /** Whether group assignments are available in the invite UI */
  allowGroups?: boolean;

  /** Test ID prefix for data-testid attributes */
  testIdPrefix: string;
}

// =============================================================================
// Permission Editor Adapter (API callbacks)
// =============================================================================

export interface PermissionEditorAdapter {
  /** Load all existing permission entries (users + groups) */
  loadEntries(): Promise<PermissionEntry[]>;

  /** Load workspace members for the invite picker */
  loadMembers(query: string): Promise<MemberInfo[]>;

  /** Load workspace groups for the invite picker */
  loadGroups(): Promise<GroupInfo[]>;

  /** Grant a role to a user */
  grantUser(userId: string, role: string): Promise<void>;

  /** Grant a role to a group */
  grantGroup(groupId: string, role: string): Promise<void>;

  /** Update an existing entry's role */
  updateRole(entry: PermissionEntry, newRole: string): Promise<void>;

  /** Atomically transfers ownership to one user; the server demotes the previous owner. */
  transferOwnership?(userId: string): Promise<void>;

  /** Revoke an entry */
  revoke(entry: PermissionEntry): Promise<void>;

  /** Present on objects workspace Mates can be granted on (pages, folders). */
  mates?: {
    /** The workspace Mates for the invite picker */
    load(): Promise<MateInfo[]>;
    /** Grant a role below Owner to a workspace Mate */
    grant(mateId: string, role: string): Promise<void>;
  };
}
